import md5 from "crypto-js/md5";
import sanitizer from "sanitize-html";
import { isEqual, cloneDeep, unset } from "lodash";
import { S3Client } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import { XhrHttpHandler } from "@aws-sdk/xhr-http-handler";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import {
  isPanelistCountValid,
  getTextLengthAsQuillJS,
  isValidURL,
  calculatePathErrors,
  getFigmaNodeIdFromUrl,
  getTestTypeForRecruit,
  getNoRecordingTestDeviceName,
  getRecordingTestDeviceName,
  getValidJSON,
} from "./common";
import {
  YESNO,
  TEST_TYPE,
  TASK_TYPES,
  MAX_LENGTH,
  DEVICE_TYPE,
  TEST_STATUS,
  LIKERT_TYPES,
  SURVEY_TYPES,
  SUCCESS_CRITERIA,
  RECRUIT_AGE_LIMITS,
  RECRUIT_CREDITS_ERR,
  RECRUIT_DEMOGRAPHICS_ERR,
  URL_PATTERN,
  TOOLKIT_TEST_STATUS,
  RECRUIT_NO_PARTICIPANT_ERR,
  RECRUIT_NATIONALITY_ERR,
  RECRUIT_SELF_SOURCED_INSUFFICEINT_ERR,
  ENTERPRISE_RECRUIT_SELF_SOURCED_INSUFFICEINT_ERR,
  ENTERPRISE_RECRUIT_CREDITS_ERR,
  PAID_RECRUIT_CREDITS_ERR,
  EXTRA_UA_CREDITS_ERR,
  TEST_TYPE_SURVEY,
  GREETING_TYPE,
  SHUFFLE_OPTIONS_TYPES,
  TASK_STATUS,
  NORECORDING_RECORDING_MODES,
  CARD_SORT_TYPES,
  DEFAULT_DEMOGRAPHICS_CONFIG,
} from "../actions/constants";
import dayjs from "./dayjs";
import { getTotalOptionsCount, getCarryOptionsRowsCount } from "./task_functions";
import { normalizeHtmlForEquality } from "../components/projectanalysis/utils";
import { flattenTasks } from "./logicUtils";

const { TEXT, LIKERT_5PT, LIKERT_7PT, LIKERT_10PT, CHECKBOX, RANKING, NOTE, SIMPLE_INPUT, MATRIX } = SURVEY_TYPES;
const { ANSWER, SUCCESS_URL, TIME_COMPLETION, RULES } = SUCCESS_CRITERIA;
const { EMPTY, DEFAULT, IMAGE, SURVEY, MOBAPP_PROTOTYPE, EXTERNAL_PROTOTYPE, GROUP, FIRSTCLICK, FIVESEC, TREE, CARDSORT, HTML_PROTOTYPE } = TASK_TYPES;
const { DRAFT, ACTIVE, COMPLETED, EDIT_TEST, INACTIVE, SAVED_TEST, UI_STATUS_EXPIRED, UI_STATUS_COMPLETED, UI_STATUS_TERMINATED } =
  TEST_STATUS;

export const sanitizedText = (textWithHTML) => sanitizer(textWithHTML, { allowedTags: [] });

export const hasDuplicateValues = (arr) => {
  return arr.reduce(({ arr, hasDuplicates }, opt) => ({ hasDuplicates: hasDuplicates || arr.includes(opt), arr: [...arr, opt] }), {
    arr: [],
    hasDuplicates: false,
  })?.hasDuplicates;
};

export const isValidTest = (
  data,
  data_launched,
  user,
  isFreePlan,
  isCustomPlan,
  hasResponseSubscription,
  responsesCollected,
  allowedFeatures,
  cancel_at,
  organization = {},
  prototypes = []
) => {
  const { test, tasks, settings, sus } = data;
  const { intro_text, complete_text, sus_status } = settings;
  const { settings: launchedSettings } = data_launched;

  if (
    isTestError(test) ||
    isTasksError(tasks, test.test_type, Number(test?.is_recording_off), prototypes, flattenTasks(tasks)) ||
    isRecruitError(
      test,
      settings,
      launchedSettings,
      user,
      isFreePlan,
      isCustomPlan,
      hasResponseSubscription,
      responsesCollected,
      allowedFeatures,
      cancel_at,
      organization
    ) ||
    isGreetingsError(intro_text, complete_text) ||
    isSUSError(sus, sus_status)
  )
    return false;

  return true;
};

// Test Validation
export function isTestError(test) {
  const { name, internal_name, nda_list = [], include_nda = YESNO.NO } = test;

  // Name
  if (!name.trim() || !internal_name.trim()) return true;

  // NDA
  const testNdaList = Array.isArray(nda_list) ? nda_list : getValidJSON(nda_list) || [];
  if (Number(include_nda) === YESNO.YES && testNdaList?.length === 0) return true;

  return false;
}

// Tasks Validation
export function isTasksError(tasks, test_type, isRecordingOff, prototypes = [], flattenedTasks = []) {
  return tasks.some((task) => validateTask(task, test_type, isRecordingOff, prototypes, flattenedTasks)?.error);
}
export function isGreetingsError(intro_text, complete_text) {
  return (
    validateGreeting(intro_text, getTestGreetingsDefault(GREETING_TYPE.WELCOME))?.error ||
    validateGreeting(complete_text, getTestGreetingsDefault(GREETING_TYPE.THANK_YOU))?.error
  );
}

export function validateGreeting(greetingText, greetingName) {
  if (!greetingText.trim() || getTextLengthAsQuillJS(greetingText) === 0)
    return { error: true, errorMsg: `Please provide a ${greetingName} message` };
  return { error: false, errorMsg: "" };
}

export function validateTask(task, test_type, isRecordingOff = false, prototypes = [], flattenedTasks = []) {
  const {
    url,
    type,
    answer,
    images,
    options,
    subTasks,
    addImages,
    start_urls,
    surveyType,
    description,
    // answer_type,
    success_type,
    five_second,
    expected_time,
    successUrlsArr,
    task_image_url,
    mobile_app_name,
    likertOptionType,
    criterias = [],
    shuffle_options,
    shuffle_criterias,
    prev_answers_config,
    tree,
    tree_answer,
    cards = [],
    categories = [],
    card_settings = {},
  } = task;

  const minLimit = Number(task?.min_limit) || 0;
  const maxLimit = Number(task?.max_limit) || 0;

  let resultObj = { error: false, errorMsg: "" };
  if (Number(task?.status) === TASK_STATUS.DELETED) {
    return resultObj;
  }

  const isSurveyTask = Number(type) === SURVEY;
  const isMultiOSApp = Number(test_type) === TEST_TYPE.MULTIOS_APP;
  const isMatrixQuestionTask = isSurveyTask && surveyType === MATRIX;
  const maxDescLength = isMatrixQuestionTask
    ? MAX_LENGTH.SURVEY_QUESTION_OLD
    : isSurveyTask
      ? MAX_LENGTH.SURVEY_QUESTION
      : MAX_LENGTH.TASK_DESCRIPTION;

  // Group Task
  if (type === GROUP) {
    if (isTasksError(subTasks, test_type, isRecordingOff, prototypes, flattenedTasks))
      resultObj = { error: true, errorMsg: "One of the subtask has an error" };
    return resultObj;
  }

  // Empty Task - No Task Type
  if (type === EMPTY) {
    resultObj = { error: true, errorMsg: "Please select a task type to continue." };
  } else if (getTextLengthAsQuillJS(description) === 0) {
    // Common Error
    resultObj = { error: true, errorMsg: "Please fill the description (atleast two characters)." };
  } else if (getTextLengthAsQuillJS(description) > maxDescLength) {
    resultObj = { error: true, errorMsg: `Task Description is too long (maximum is ${maxDescLength} characters).` };
  } else if (type === DEFAULT && success_type === TIME_COMPLETION && Number(expected_time) < 5) {
    resultObj = { error: true, errorMsg: "Please fill the expected time." };
  } else if (type === DEFAULT && !isMultiOSApp) {
    // Default Task
    if (url?.trim() === "" || !isValidURL(url)) {
      resultObj = { error: true, errorMsg: `Please fill the ${url?.trim() === "" ? "website" : "valid"} URL` };
    } else if (success_type === ANSWER && (!answer || answer?.toString().trim() === "")) {
      resultObj = { error: true, errorMsg: "Please fill the answer." };
    } else if (
      success_type === SUCCESS_URL &&
      successUrlsArr.some(({ url, urlpattern }) => url === "" || (Number(urlpattern) === URL_PATTERN.EXACT && !isValidURL(url)))
    ) {
      resultObj = { error: true, errorMsg: "Please fill the success urls." };
      // } else if (success_type === SUCCESS_URL && successUrlsArr.some((s) => !isSameURL(url, s.url))) {
      //   resultObj = { error: true, errorMsg: "Success url should be the same as the website url." };
    } else if (
      [TEST_TYPE.ANDROID_APP, TEST_TYPE.IOS_APP].includes(Number(test_type)) &&
      (url?.trim() === "" || mobile_app_name?.trim() === "")
    ) {
      // Mobile App
      resultObj = { error: true, errorMsg: `Please fill the ${url?.trim() === "" ? "Link to Your Mobile App." : "App Name."}` };
    }
  } else if ([IMAGE, FIRSTCLICK, FIVESEC].includes(type) && !task_image_url) {
    // Image Task
    resultObj = { error: true, errorMsg: "Please upload a image." };
  } else if (
    (type === MOBAPP_PROTOTYPE && (url?.trim() === "" || mobile_app_name?.trim() === "")) ||
    (type === DEFAULT && [TEST_TYPE.MULTIOS_APP].includes(Number(test_type)) && mobile_app_name?.trim() === "")
  ) {
    // Mobile App
    resultObj = { error: true, errorMsg: `Please fill the ${url?.trim() === "" ? "Link to Your Mobile App." : "App Name."}` };
  } else if (type === SURVEY) {
    // Survey Task
    if (addImages && images.length === 0) resultObj = { error: true, errorMsg: "Please upload the images." };

    if (
      surveyType !== TEXT &&
      surveyType !== NOTE &&
      surveyType !== SIMPLE_INPUT &&
      (![LIKERT_5PT, LIKERT_7PT, LIKERT_10PT, MATRIX].includes(surveyType) ||
        ([LIKERT_5PT, LIKERT_7PT, LIKERT_10PT, MATRIX].includes(surveyType) && likertOptionType === LIKERT_TYPES.CUSTOM))
    ) {
      if (hasDuplicateValues(options?.map(({ name }) => name))) {
        resultObj = { error: true, errorMsg: "Please remove duplicate options." };
      }
      if (options.some(({ name }) => String(name ?? "").trim() === ""))
        resultObj = { error: true, errorMsg: "Please fill all the options." };
      // If shuffle_options is (shuffle with some fixed)
      if (shuffle_options === Number(SHUFFLE_OPTIONS_TYPES.SHUFFLE_SPECIFIC_OPTIONS)) {
        const fixedError = validateFixedItems(options, surveyType === MATRIX ? "column" : "option");
        if (fixedError) resultObj = fixedError;
      }
    }
    if (surveyType === MATRIX && Number(shuffle_criterias) === SHUFFLE_OPTIONS_TYPES.SHUFFLE_SPECIFIC_OPTIONS) {
      const fixedError = validateFixedItems(criterias, "row");
      if (fixedError) resultObj = fixedError;
    }

    if (
      (surveyType === CHECKBOX || surveyType === RANKING) &&
      options.length < 2 &&
      getCarryOptionsRowsCount(task)["optionCount"] === 0
    )
      resultObj = { error: true, errorMsg: "Please add at least two options." };
    if (surveyType === MATRIX) {
      if (options.length < 2 && getCarryOptionsRowsCount(task)["optionCount"] === 0) {
        resultObj = { error: true, errorMsg: "Please add at least two columns." };
      }
      if (criterias.length < 1 && getCarryOptionsRowsCount(task)["rowCount"] === 0) {
        resultObj = { error: true, errorMsg: "Please add at least one row." };
      }
      if (hasDuplicateValues(criterias?.map(({ name }) => name))) {
        resultObj = { error: true, errorMsg: "Please remove duplicate row labels." };
      }
      if (criterias.some(({ name }) => String(name ?? "").trim() === ""))
        resultObj = { error: true, errorMsg: "Please fill all the row labels." };
    }

    if (surveyType === CHECKBOX && (minLimit > 0 || maxLimit > 0)) {
      if (minLimit > 0 && maxLimit > 0 && minLimit > maxLimit) {
        resultObj = { error: true, errorMsg: "Min limit should be less than or equal to max limit." };
      }

      if (!maxLimit && minLimit > getTotalOptionsCount(task, flattenedTasks)) {
        resultObj = { error: true, errorMsg: "Min limit should be less than or equal to number of options." };
      }
    }
  } else if (type === EXTERNAL_PROTOTYPE) {
    // Prototype Task
    if (!url?.trim()) resultObj = { error: true, errorMsg: "Please add or select a prototype." };
    else if ((success_type === TIME_COMPLETION || Number(five_second) === 1) && Number(expected_time) < 5) {
      resultObj = { error: true, errorMsg: "Please fill the expected time." };
    } else if (success_type === SUCCESS_URL && successUrlsArr?.length === 0) {
      resultObj = { error: true, errorMsg: "Please add at least one success path." };
    } else if (success_type === SUCCESS_URL && successUrlsArr.some(({ url }) => !url || url?.trim() === "")) {
      resultObj = { error: true, errorMsg: "Please select end screen for all success paths." };
    } else if (
      success_type === SUCCESS_URL &&
      successUrlsArr.some(
        ({ url, urlpattern }, i) =>
          Number(urlpattern) === URL_PATTERN.PATH_URLS &&
          calculatePathErrors(successUrlsArr, {
            currentPathIndex: i,
            currentPathIds: JSON.parse(url.replace(/http:\/\/www\./, "https://"))
              ?.map(getFigmaNodeIdFromUrl)
              ?.filter(Boolean),
          })?.hasError
      )
    ) {
      resultObj = { error: true, errorMsg: "Please resolve the path errors before proceeding." };
    } else if (success_type === ANSWER && (!answer || answer.toString().trim() === "")) {
      resultObj = { error: true, errorMsg: "Please fill the answer." };
    } else if (success_type === TIME_COMPLETION && Number(expected_time) < 5) {
      resultObj = { error: true, errorMsg: "Please fill the expected time." };
    }

    // check if selected prototype has frames
    if (task?.prototype_id && prototypes?.length > 0) {
      const activePrototype = prototypes?.find((p) => Number(p?.id) === Number(task?.prototype_id));
      if (activePrototype && (!activePrototype?.frames || activePrototype?.frames?.length === 0)) {
        resultObj = {
          error: true,
          errorMsg:
            "We couldn't detect any navigable screens or frames in your prototype. To continue, please ensure your prototype contains at least one valid screen or structured frame.",
        };
      }
    }
  } else if (isMultiOSApp && type === DEFAULT) {
    if (mobile_app_name?.trim() === "") {
      resultObj = { error: true, errorMsg: "Please fill the App Name." };
    } else if (start_urls?.ios?.trim() === "" || !isValidURL(start_urls?.ios)) {
      resultObj = { error: true, errorMsg: "Please fill the Link to Your iOS Mobile App." };
    } else if (start_urls?.android?.trim() === "" || !isValidURL(start_urls?.android)) {
      resultObj = { error: true, errorMsg: "Please fill the Link to Your Android Mobile App." };
    }
  } else if (prev_answers_config?.enabled) {
    if (prev_answers_config?.task_ids.length === 0) {
      resultObj = { error: true, errorMsg: "Please select previous question whose options are required to be carry forward" };
    }
  } else if (type === HTML_PROTOTYPE) {
    const rules = getValidJSON(task?.proto_rules) || {};
    const ruleCount = (condition) => (condition ? (condition.all || condition.any || [condition]).length : 0);
    // A condition row left blank in the rules editor, e.g. { reached: "" }
    const hasBlank = (condition) =>
      !!condition &&
      (["reached", "clicked", "event"].some((k) => k in condition && !String(condition[k] ?? "").trim()) ||
        [...(condition.all || []), ...(condition.any || []), ...(condition.not ? [condition.not] : [])].some(hasBlank));
    if (!task?.html_prototype_id || !url?.trim()) {
      resultObj = { error: true, errorMsg: "Please add the prototype link and check it." };
    } else if (success_type === RULES && (hasBlank(rules.success) || hasBlank(rules.fail))) {
      resultObj = { error: true, errorMsg: "Please fill in or remove the empty success rules." };
    } else if (success_type === RULES && ruleCount(rules.success) + ruleCount(rules.fail) === 0) {
      resultObj = { error: true, errorMsg: "Please add at least one success or fail rule, or choose No Criteria." };
    } else if (success_type === RULES && rules.timeLimitSec !== undefined && rules.timeLimitSec !== null && Number(rules.timeLimitSec) < 5) {
      resultObj = { error: true, errorMsg: "The time limit must be at least 5 seconds." };
    }
  } else if (type === TREE) {
    const treeAnswers = getValidJSON(tree_answer) || [];
    if (tree?.treeData?.length === 0) {
      resultObj = { error: true, errorMsg: "Please create the tree to proceed." };
    } else if (treeAnswers.length === 0) {
      resultObj = { error: true, errorMsg: "Please define answers to evaluate task success." };
    }
  } else if (type === CARDSORT) {
    const cardSettings = getValidJSON(card_settings) || {};
    const cardsError = getCatCardsError(cards, true);

    // No Cards/Blank Entry/Duplicate Entry
    if (cardsError?.hasError) {
      resultObj = { error: true, errorMsg: cardsError?.errorMsg };
    }

    const catsError = getCatCardsError(categories);
    // Categories Required in Closed CardSort
    if (cardSettings?.type === CARD_SORT_TYPES.CLOSED && catsError?.hasError) {
      resultObj = { error: true, errorMsg: catsError?.errorMsg };
    } else if (cardSettings?.type === CARD_SORT_TYPES.HYBRID && catsError?.hasBlankDuplicateError) {
      // Mixed Card Sort, check only for duplicate or blank if exists
      resultObj = { error: true, errorMsg: catsError?.blankDuplicateErrorMsg };
    }
  }

  return resultObj;
}

export function getCatCardsError(items = [], isCard = false) {
  const noItems = items?.length === 0; // No Items
  const hasBlankEntry = items?.some((item) => String(item?.name ?? "").trim() === ""); // Blank Entry
  const hasDuplicateEntry = hasDuplicateValues(items?.map(({ name }) => name)); // Duplicate Entry

  // Blank Duplicate Error Message
  const blankDuplicateErrorMsg = hasBlankEntry
    ? `Please fill all the ${isCard ? "card" : "category"} labels.`
    : `${isCard ? "Cards" : "Categories"} must be unique. Edit or remove duplicate items`;

  return {
    noItems,
    hasBlankEntry,
    hasDuplicateEntry,
    blankDuplicateErrorMsg,
    hasError: noItems || hasBlankEntry || hasDuplicateEntry,
    hasBlankDuplicateError: hasBlankEntry || hasDuplicateEntry,
    errorMsg: noItems ? `You need to set at least one ${isCard ? "card" : "category"}` : blankDuplicateErrorMsg,
  };
}

export function validateFixedItems(items, label) {
  const fixedItems = items.filter((item) => item?.is_fixed);
  // At least one fixed
  if (fixedItems.length === 0) {
    return { error: true, errorMsg: `Select options to be pinned within list` };
  }

  // All fixed
  if (fixedItems.length === items.length) {
    return { error: true, errorMsg: `Unpin at least one ${label} or turn off Shuffle ${capitalize(label)}s` };
  }

  // Only one non-fixed
  /*if (items.length - fixedItems.length === 1) {
    return { error: true, errorMsg: `At least two ${label}s should not be fixed.` };
  }*/
  return null; // No error
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Settings Validation
export function isRecruitError(
  test,
  settings,
  launchedSettings,
  user,
  isFreePlan,
  isCustomPlan,
  hasResponseSubscription,
  responsesCollected,
  allowedFeatures,
  cancel_at,
  organization = {}
) {
  const { users_max, visitors_max, ux_testers_nationality } = settings || {};
  const { credits_panel, credits_response_total } = organization || {};
  const testType = getTestTypeForRecruit(test);
  const credits = user?.credits?.[testType];

  const {
    ua_panel: { enabled: isUAPanelEnabled = true },
    ua_panel_topup: { enabled: isUAPanelTopupEnabled = true },
  } = allowedFeatures || {};

  const LOWER_AGE = {
    MIN: RECRUIT_AGE_LIMITS.MIN,
    MAX: launchedSettings?.age_min ? Number(launchedSettings.age_min) : RECRUIT_AGE_LIMITS.MAX,
  };
  const UPPER_AGE = {
    MIN: launchedSettings?.age_max ? Number(launchedSettings.age_max) : RECRUIT_AGE_LIMITS.MIN,
    MAX: RECRUIT_AGE_LIMITS.MAX,
  };
  const minAge = launchedSettings?.age_min ? Number(launchedSettings.age_min) : Number(settings.age_min);
  const maxAge = launchedSettings?.age_max ? Number(launchedSettings.age_max) : Number(settings.age_max);

  if (users_max === 0 && visitors_max === 0) {
    return RECRUIT_NO_PARTICIPANT_ERR;
  }

  if (Number(users_max) > 0 && !ux_testers_nationality) {
    return RECRUIT_NATIONALITY_ERR;
  }

  const estimatedPlatformCredits =
    Number(visitors_max + users_max - Number(responsesCollected?.panelTesters) - Number(responsesCollected?.ownTesters)) *
    Number(credits?.random);
  const insufficientPlatformCredits = Math.abs(estimatedPlatformCredits) - Number(credits_response_total) > 0;
  if (isFreePlan && !hasResponseSubscription && insufficientPlatformCredits) {
    return RECRUIT_SELF_SOURCED_INSUFFICEINT_ERR;
  }
  if ((isCustomPlan || !hasResponseSubscription || (hasResponseSubscription && cancel_at)) && insufficientPlatformCredits) {
    return ENTERPRISE_RECRUIT_SELF_SOURCED_INSUFFICEINT_ERR;
  }

  const extraUAs = Number(users_max) - Number(responsesCollected?.panelTesters || 0);
  if (!isUAPanelEnabled && extraUAs > 0) {
    return EXTRA_UA_CREDITS_ERR;
  }

  const insufficientUACredits = !isPanelistCountValid(responsesCollected, users_max, credits?.uxarmy, credits_panel);
  // free
  if (!isUAPanelEnabled && insufficientUACredits) {
    return RECRUIT_CREDITS_ERR;
  }
  // enterprise
  if (isUAPanelEnabled && !isUAPanelTopupEnabled && insufficientUACredits) {
    return ENTERPRISE_RECRUIT_CREDITS_ERR;
  }
  // paid
  if (isUAPanelEnabled && isUAPanelTopupEnabled && insufficientUACredits) {
    return PAID_RECRUIT_CREDITS_ERR;
  }

  if (!(minAge < maxAge && minAge >= LOWER_AGE.MIN && minAge <= LOWER_AGE.MAX && maxAge >= UPPER_AGE.MIN && maxAge <= UPPER_AGE.MAX)) {
    return RECRUIT_DEMOGRAPHICS_ERR;
  }

  return false;
}

const keysToIgnore = [
  "published",
  "shuffle_status",
  "sus_status",
  "email_lang",
  "intro_text",
  "complete_text",
  "quotafull_text",
  "disqualification_text",
  "activeID",
];
const removeKeys = (obj, keys) => {
  keys.forEach((key) => {
    unset(obj, key);
  });

  if (obj && obj?.test) {
    unset(obj.test, "isLogicOpenedFirstTime");
    unset(obj.test, "sync_name");
  }

  if (obj && obj.tasks && obj.tasks.length) {
    obj.tasks.forEach((task) => {
      unset(task, "chosen");
      unset(task, "selected");
      unset(task, "isNew");
      unset(task, "use_options_as_img");

      if (typeof task.description === "string") {
        task.description = normalizeHtmlForEquality(task.description);
      }
    });
  }

  // Screener
  if (obj && obj.questions) {
    obj.questions.forEach((question) => {
      unset(question, "chosen");
      unset(question, "selected");
    });
  }
  return obj;
};
export const isStateUnchangedWithIgnoredKeys = (currentState, publishedState) => {
  const filteredCurrentState = removeKeys(cloneDeep(currentState), keysToIgnore);
  const filteredPublishedState = removeKeys(cloneDeep(publishedState), keysToIgnore);
  return isEqual(filteredCurrentState, filteredPublishedState);
};

// no use now as backend is sending the information
export const getTestStatus = (testStatus) => {
  const status = {
    [DRAFT]: {
      label: "Draft",
      icon: "testDraft",
      className: "color-365198 border-365198 bg-white-6",
      classNameNew: "bg-EEF2F6 color-364152 br-8px fw-400 border-0 p-4-8",
    },
    [EDIT_TEST]: {
      label: "Draft",
      icon: "testDraft",
      className: "color-365198 border-365198 bg-white-6",
      classNameNew: "bg-EEF2F6 color-364152 br-8px fw-400 border-0 p-4-8",
    },
    [SAVED_TEST]: {
      label: "Draft",
      icon: "testDraft",
      className: "color-365198 border-365198 bg-white-6",
      classNameNew: "bg-EEF2F6 color-364152 br-8px fw-400 border-0 p-4-8",
    },
    [ACTIVE]: {
      label: "Active",
      icon: "testActive",
      className: "color-0B3FE6 border-0b3fe6 bg-F5F8FF",
      classNameNew: "bg-F0FAFF text-2E62E9 br-8px fw-400 border-0 p-4-8",
    },
    [INACTIVE]: {
      label: "Pause",
      icon: "testPause",
      className: "color-365198 border-365198 bg-white-6",
      classNameNew: "bg-EEF2F6 color-364152 br-8px fw-400 border-0 p-4-8",
    },
    [COMPLETED]: {
      label: "Completed",
      icon: "testCompleted",
      className: "color-46AA58 border-46AA58 bg-46AA58_01",
      classNameNew: "bg-EEFBF4 color-17663A br-8px fw-400 border-0 p-4-8",
    }, // same for UI_STATUS_COMPLETED
    [UI_STATUS_EXPIRED]: {
      label: "Expired",
      icon: "testExpired",
      className: "color-365198 border-365198 bg-white-6",
      classNameNew: "bg-EEF2F6 color-364152 br-8px fw-400 border-0 p-4-8",
    },
    [UI_STATUS_TERMINATED]: {
      label: "Terminated",
      icon: "testTerminated",
      className: "color-365198 border-365198 bg-white-6",
      classNameNew: "bg-EEF2F6 color-364152 br-8px fw-400 border-0 p-4-8",
    },
  };

  return status[testStatus === UI_STATUS_COMPLETED ? COMPLETED : testStatus];
};

export const generateSlug = (randomNumber) => {
  const timestamp = Date.now();
  const uniquePart = `${randomNumber}-${timestamp}`;
  const hash = md5(uniquePart).toString();
  const slug = hash.substr(0, 8);
  return slug;
};

// Get dayjs date
export const getFormatedDate = (d = dayjs()) => d.format("YYYY-MM-DD HH:mm:ss");

export const moderatedTestAddExtraFields = (test, increaseResponses = false) => {
  const sessions = test?.sessions || [];
  let total_sessions = Number(test?.total_sessions) || 0;
  let total_completed_sessions = Number(test?.total_completed_sessions) || 0;
  const total_active_sessions = Number(test?.total_active_sessions) || 0;

  // Increase the total sessions and completed sessions
  if (increaseResponses) {
    total_sessions += 1;
    total_completed_sessions += 1;
  }

  return {
    ...test,
    sessions,
    total_sessions,
    total_completed_sessions,
    hasSessions: sessions.length > 0,
    isCompleted: Number(test?.status) === COMPLETED,
    start_date: getFormatedDate(dayjs(test?.start_date)),
    isCollabTest: Number(test?.is_collab_test) === YESNO.YES,
    transcription_enabled: Number(test?.transcription_enabled),
    hasCollaborator: Number(test?.has_collaborator) === YESNO.YES,
    isInstantSession: Number(test?.test_type) === TEST_TYPE.INSTANT_SESSION,
    totalActiveCompletedSessions: total_active_sessions + total_completed_sessions,
    end_date: !test?.end_date || test?.end_date === "0000-00-00 00:00:00" ? "" : getFormatedDate(dayjs(test?.end_date)),
  };
};

// Upload File to S3
export const uploadFileToS3 = async (file, awsParams, onProgress = () => {}) => {
  const { filePath, region, bucketName, identityPoolId } = awsParams;

  // Initialize the Amazon Cognito credentials provider
  const client = new S3Client({
    region,
    requestHandler: new XhrHttpHandler({}),
    credentials: fromCognitoIdentityPool({ identityPoolId, client: new CognitoIdentityClient({ region }) }),
    // CRITICAL: This is the only way to bypass the CRC32 part 1 error in newer SDKs
    requestChecksumCalculation: "WHEN_REQUIRED",
    responseChecksumValidation: "WHEN_REQUIRED",
  });

  // Upload Params
  const params = {
    Body: file,
    Key: filePath,
    Bucket: bucketName,
    ACL: "public-read",
    ContentType: file.type,
    ChecksumAlgorithm: null,
    ContentDisposition: "attachment",
    queueSize: navigator?.hardwareConcurrency || 2,
  };

  try {
    const S3Upload = new Upload({ client, params });

    // Start tracking the UploadProgress
    S3Upload.on("httpUploadProgress", (e) => onProgress(Math.ceil((e?.loaded / e?.total) * 100)));

    return await S3Upload.done();
  } catch (e) {
    return e;
  }
};

// Timezone string
export const getTimezoneString = (timezone = dayjs.tz.guess()) => `GMT ${dayjs().tz(timezone).format("Z")} ${timezone}`;

// Get Toolkit Status As Yii Status's
export const getToolkitStatusAsYii = (toolkitStatus = TOOLKIT_TEST_STATUS?.DRAFT) => {
  if (toolkitStatus === TOOLKIT_TEST_STATUS?.DRAFT) return TEST_STATUS?.DRAFT;
  if (toolkitStatus === TOOLKIT_TEST_STATUS?.ACTIVE) return TEST_STATUS?.ACTIVE;
  if (toolkitStatus === TOOLKIT_TEST_STATUS?.EDIT) return TEST_STATUS?.EDIT_TEST;
  if (toolkitStatus === TOOLKIT_TEST_STATUS?.INACTIVE) return TEST_STATUS?.INACTIVE;
  if (toolkitStatus === TOOLKIT_TEST_STATUS?.COMPLETED) return TEST_STATUS?.COMPLETED;
  if (toolkitStatus === TOOLKIT_TEST_STATUS?.DELETED) return TEST_STATUS?.UI_STATUS_TERMINATED;

  return toolkitStatus;
};

/**
 * Returns human-readable target platform/device string
 * @param {string|number} testType
 * @param {string|number} deviceType
 * @returns {string}
 */
export function getTargetDevicesAndPlatforms(test) {
  const type = Number(test?.test_type);
  const device = Number(test?.testing_device);
  const isSurveyTest = test?.type === TEST_TYPE_SURVEY;
  const isPrototype = Number(test?.pcheck) === YESNO.YES;
  const isRecordingOff = Number(test?.is_recording_off) === YESNO.YES;

  const platformMap = {
    [TEST_TYPE.IOS_APP]: "iOS",
    [TEST_TYPE.IOS_SDK]: "iOS",
    [TEST_TYPE.WEBSITE]: "Website",
    [TEST_TYPE.ANDROID_APP]: "Android",
    [TEST_TYPE.ANDROID_SDK]: "Android",
    [TEST_TYPE.MULTIOS_APP]: "iOS & Android",
  };

  const deviceMap = {
    [DEVICE_TYPE.COMPUTER]: "Desktop",
    [DEVICE_TYPE.TABLET]: "Tablet",
    [DEVICE_TYPE.MOBILE]: "Mobile",
    [DEVICE_TYPE.TABLET_AND_MOBILE]: "Tablet & Mobile",
    [DEVICE_TYPE.TABLET_IPAD]: "iPad",
    [DEVICE_TYPE.ANY]: "Any Device",
  };

  const platform = platformMap[type] || "Unknown Platform";
  const deviceName = deviceMap[device] || "Unknown Device";

  return isSurveyTest || isRecordingOff ? deviceName : `${deviceName}, ${isPrototype ? "Prototype" : platform}`;
}

// Has Error in SUS Questions
export function isSUSError(sus = [], sus_status = YESNO.NO) {
  return parseInt(sus_status) === YESNO.YES && sus?.some((q) => !q?.title || !q?.title?.trim());
}

export const getTestGreetingsDefault = (greetingType) => {
  const greeting = {
    [GREETING_TYPE.WELCOME]: {
      label: "Welcome",
    },
    [GREETING_TYPE.THANK_YOU]: {
      label: "Thank you",
    },
    [GREETING_TYPE.QUOTAFULL]: {
      label: "Quota Full",
    },
    [GREETING_TYPE.DISQUALIFICATION]: {
      label: "Disqualification",
    },
  };
  return greeting[greetingType];
};

export const validateRedirectUrl = (url) => {
  if (!url || url.trim() === "") {
    return "Please enter a valid URL";
  }
  if (!isValidURL(url)) {
    return "Please enter a valid URL";
  }
  try {
    const queryString = url.split("?")[1];
    const forbiddenParams = ["lang", "panel", "logid", "response_id", "responseid", "preview", "published"];

    if (queryString) {
      const params = new URLSearchParams(queryString);

      for (let [key, val] of params.entries()) {
        const trimmedKey = key.trim();
        const trimmedVal = val.trim();

        const match = trimmedVal.match(/^\{(.+?)\}$/);
        const isVariable = !!match;
        const variable = isVariable ? match[1].trim() : null;

        if (!isVariable && forbiddenParams.includes(trimmedKey)) {
          return `Query parameter key '${trimmedKey}' is not allowed with static value.`;
        }
        if (isVariable && forbiddenParams.includes(variable)) {
          return `Query parameter value '{${variable}}' is not allowed.`;
        }
      }
    }
  } catch {
    return "Invalid URL format";
  }
  return null; // no error
};

export const validateHtmlText = (val) => {
  const rawHtml = val || "";
  const plainText = rawHtml.replace(/<(.|\n)*?>/g, "").trim();

  if (!plainText) {
    return "This field cannot be empty";
  } else if (getTextLengthAsQuillJS(rawHtml.trim()) < 2) {
    return "Add at least 3 characters";
  }
  return null; // no error
};

export function getPlatformDevice(test) {
  const type = Number(test?.test_type);
  const device = Number(test?.testing_device);
  const isSurveyTest = test?.type === TEST_TYPE_SURVEY;
  const isPrototype = Number(test?.pcheck) === YESNO.YES;
  const isRecordingOff = Number(test?.is_recording_off) === YESNO.YES;

  const platformMap = {
    [TEST_TYPE.IOS_APP]: "iOS",
    [TEST_TYPE.IOS_SDK]: "iOS",
    [TEST_TYPE.WEBSITE]: "Website",
    [TEST_TYPE.ANDROID_APP]: "Android",
    [TEST_TYPE.ANDROID_SDK]: "Android",
    [TEST_TYPE.MULTIOS_APP]: "iOS & Android",
  };

  const deviceMap = {
    [DEVICE_TYPE.COMPUTER]:
      isSurveyTest || isRecordingOff
        ? getNoRecordingTestDeviceName(DEVICE_TYPE.COMPUTER)
        : getRecordingTestDeviceName(DEVICE_TYPE.COMPUTER),
    [DEVICE_TYPE.TABLET]:
      isSurveyTest || isRecordingOff
        ? getNoRecordingTestDeviceName(DEVICE_TYPE.TABLET)
        : getRecordingTestDeviceName(DEVICE_TYPE.TABLET),
    [DEVICE_TYPE.MOBILE]:
      isSurveyTest || isRecordingOff
        ? getNoRecordingTestDeviceName(DEVICE_TYPE.MOBILE)
        : getRecordingTestDeviceName(DEVICE_TYPE.MOBILE),
    [DEVICE_TYPE.TABLET_AND_MOBILE]:
      isSurveyTest || isRecordingOff
        ? getNoRecordingTestDeviceName(DEVICE_TYPE.TABLET_AND_MOBILE)
        : getRecordingTestDeviceName(DEVICE_TYPE.TABLET_AND_MOBILE),
    [DEVICE_TYPE.TABLET_IPAD]:
      isSurveyTest || isRecordingOff
        ? getNoRecordingTestDeviceName(DEVICE_TYPE.TABLET_IPAD)
        : getRecordingTestDeviceName(DEVICE_TYPE.TABLET_IPAD),
    [DEVICE_TYPE.ANY]:
      isSurveyTest || isRecordingOff ? getNoRecordingTestDeviceName(DEVICE_TYPE.ANY) : getRecordingTestDeviceName(DEVICE_TYPE.ANY),
  };

  const platform = platformMap[type] || "Unknown Platform";
  const deviceName = deviceMap[device] || "Unknown Device";

  const tbtLabel = `${deviceName} ${isPrototype ? "Prototype" : platform}`;

  return isSurveyTest ? "Survey (No recording)" : isRecordingOff ? "Unmoderated Testing (In-Browser)" : tbtLabel;
}

// GET NO RECORDING MODES
export const getNoRecordingModes = () => {
  const { NO, YES } = YESNO;

  return [
    {
      label: "None",
      value: NORECORDING_RECORDING_MODES.NONE,
      settings: { audio: NO, camera: NO, screen: NO },
    },
    {
      label: "Camera and Audio",
      value: NORECORDING_RECORDING_MODES.CAMERA_AND_AUDIO,
      settings: { audio: YES, camera: YES, screen: NO },
    },
    {
      label: "Audio Only",
      value: NORECORDING_RECORDING_MODES.AUDIO_ONLY,
      settings: { audio: YES, camera: NO, screen: NO },
    },
  ];
};

// GET NO RECORDING MODE BY VALUE
export const getNoRecordingMode = (mode = NORECORDING_RECORDING_MODES.NONE) => {
  const modes = getNoRecordingModes();
  return modes.find(({ value }) => value === mode) || null;
};

// GET NO RECORDING MODE BY SETTINGS
export const getNoRecordingModeBySettings = (settings = {}) => {
  const modes = getNoRecordingModes();

  return (
    modes.find(
      ({ settings: modeSettings }) =>
        modeSettings.audio === Number(settings?.audio) && modeSettings.camera === Number(settings?.camera)
    )?.value || modes[0]?.value
  );
};

// Parse demographics_config from backend (JSON string, array, or null) into a normalised array.
// Always guarantees every field in DEFAULT_DEMOGRAPHICS_CONFIG is present (an id missing from a stored,
// pre-existing config — e.g. "email" on any test configured before it existed — keeps that field's own
// default `enabled`, not the other fields' `enabled: true`); falls back to the defaults for old tests.
export const parseDemographicsConfig = (raw) => {
  const parsed = getValidJSON(raw);
  if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_DEMOGRAPHICS_CONFIG;
  const storedById = {};
  parsed.forEach((f) => {
    if (f.id) storedById[f.id] = f;
  });
  return DEFAULT_DEMOGRAPHICS_CONFIG.map((def) => ({
    ...def,
    enabled: storedById[def.id] !== undefined ? !!storedById[def.id].enabled : def.enabled,
  }));
};
